import { MAX_ROOM_CAPACITY, type RoomDto } from '@room-mates/shared';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button, Dialog, ErrorAlert, Field, Select, TextInput } from '../../shared/ui/index.js';
import { useCreateRoom, useHouses, useUpdateRoom } from './houseHooks.js';

export interface RoomDialogProps {
  /** Fixed when the dialog is opened from a house; absent on the rooms page. */
  readonly houseId?: string;
  readonly room?: RoomDto;
  readonly onClose: () => void;
}

/**
 * Adds or edits a room, from inside a house or from the rooms list.
 *
 * A room cannot exist without a house — its name has to be unique within one,
 * and its beds are numbered per house — so creating one from the flat list asks
 * which house it belongs to rather than leaving it unattached. The house is
 * fixed once the room exists: moving a room would strand its beds' numbers.
 */
export function RoomDialog({ houseId, room, onClose }: RoomDialogProps): ReactElement {
  const t = useTranslation();
  const houses = useHouses();
  const [selectedHouseId, setSelectedHouseId] = useState(room?.houseId ?? houseId ?? '');
  const [name, setName] = useState(room?.name ?? '');
  const [maxCapacity, setMaxCapacity] = useState(String(room?.maxCapacity ?? 2));

  const createRoom = useCreateRoom(selectedHouseId);
  const updateRoom = useUpdateRoom(selectedHouseId);
  const mutation = room === undefined ? createRoom : updateRoom;
  const fieldError = useFieldErrors(mutation.error);

  const housePickerIsOpen = room === undefined && houseId === undefined;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const payload = { name, maxCapacity: Number(maxCapacity) };
    if (room === undefined) {
      createRoom.mutate(payload, { onSuccess: onClose });
      return;
    }
    updateRoom.mutate({ roomId: room.id, input: payload }, { onSuccess: onClose });
  };

  return (
    <Dialog
      title={room === undefined ? t.roomDialog.addTitle : t.roomDialog.editTitle(room.name)}
      onClose={onClose}
    >
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={mutation.error} />

        {housePickerIsOpen ? (
          <Field
            label={t.roomDialog.houseLabel}
            hint={t.roomDialog.houseHint}
            error={fieldError('houseId')}
          >
            {(props) => (
              <Select
                {...props}
                value={selectedHouseId}
                onChange={(event) => setSelectedHouseId(event.target.value)}
                required
              >
                <option value="">{t.roomDialog.chooseHouse}</option>
                {(houses.data ?? []).map((house) => (
                  <option key={house.id} value={house.id}>
                    {house.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        <Field
          label={t.roomDialog.nameLabel}
          hint={t.roomDialog.nameHint}
          error={fieldError('name')}
        >
          {(props) => (
            <TextInput
              {...props}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus={!housePickerIsOpen}
              required
            />
          )}
        </Field>

        <Field
          label={t.roomDialog.maxPeopleLabel}
          hint={t.roomDialog.maxPeopleHint}
          error={fieldError('maxCapacity')}
        >
          {(props) => (
            <TextInput
              {...props}
              type="number"
              min={1}
              max={MAX_ROOM_CAPACITY}
              value={maxCapacity}
              onChange={(event) => setMaxCapacity(event.target.value)}
              required
            />
          )}
        </Field>

        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button type="submit" loading={mutation.isPending} disabled={selectedHouseId === ''}>
            {room === undefined ? t.roomDialog.addRoom : t.roomDialog.saveRoom}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
